// Pure helpers for the "Import the personnel too (AI)" flow — turning the AI's
// `proposedPersonnel` payload (musicians + instruments, crew + role) into a
// reviewable diff against the band's EXISTING rider lists, and applying the
// user's checked choices to a LOCAL copy of those lists (the caller then saves
// via the existing versioned PUT helpers).
//
// SAFETY CONTRACT (non-negotiable):
//   - a field that is already filled is NEVER overwritten — it is listed in
//     `conflicts` for information, and only applied if the user explicitly
//     checks that conflict row (unchecked by default);
//   - applying the same proposal twice creates NO duplicate (idempotent);
//   - empty proposals change nothing;
//   - new cards are built with createMusician()/createInstrument()/
//     createCrewMember() so they are complete and valid for the frontend.
//
// Kept dependency-free and side-effect-free so it is unit-testable outside React.

import {
  createMusician,
  createInstrument,
  createCrewMember,
  type Musician,
  type CrewMember,
} from '../types';

// ------------------------------------------------------------------ types --

export interface ProposedInstrument {
  name: string;
  channels?: number;
}

export interface ProposedMusician {
  name: string;
  instruments?: (ProposedInstrument | string)[];
}

export interface ProposedCrew {
  name: string;
  role?: string;
}

export interface ProposedPersonnel {
  musicians?: ProposedMusician[];
  crew?: ProposedCrew[];
}

export type PersonnelKind = 'musician' | 'crew';

export interface PersonnelMatch {
  kind: PersonnelKind;
  /** 'enrich' = a same-named card already exists (fill only its empty fields);
   *  'add' = no match, a new card will be created. */
  action: 'enrich' | 'add';
  /** The existing card id when action === 'enrich'. */
  targetId?: string;
  /** The fields that will be written (only empty fields of the target). */
  fields: Record<string, unknown>;
  /** Fields that are already filled and would NOT be overwritten (info only). */
  conflicts: string[];
  /** The proposed person (name + instruments/role) for the UI row. */
  proposed: ProposedMusician | ProposedCrew;
}

export interface PersonnelDiff {
  musicians: PersonnelMatch[];
  crew: PersonnelMatch[];
}

// ------------------------------------------------------------- name match --

// Normalize a person's name for matching: lowercase, strip accents, collapse
// whitespace, drop parentheses and their content.
export function normalizePersonName(name: string): string {
  return (name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip combining accents
    .replace(/\([^)]*\)/g, ' ')      // drop parenthesized content
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

// ------------------------------------------------------------- role match --

// The closed set of crew roles actually used by the app (mirrors CrewRole in
// src/types.ts). Unknown roles are mapped to 'other'.
const KNOWN_CREW_ROLES = [
  'crew', 'driver', 'foh', 'guitar tech', 'lights', 'manager', 'mon',
  'other', 'stagehand', 'tour manager', 'video',
];

export function normalizeCrewRole(role: string | undefined): string {
  if (typeof role !== 'string') return 'other';
  const r = role.trim();
  if (!r) return 'other';
  const lower = r.toLowerCase();
  if (lower === 'front of house engineer' || lower === 'foh engineer' || lower === 'front of house') return 'foh';
  if (lower === 'monitor engineer' || lower === 'mon engineer') return 'mon';
  if (lower === 'tourmanager' || lower === 'tm') return 'tour manager';
  if (lower === 'guitar technician') return 'guitar tech';
  if (KNOWN_CREW_ROLES.includes(lower)) return lower;
  return 'other';
}

// ------------------------------------------------------------- instruments --

// Normalize a proposed instrument (string OR { name, channels? }) into a
// { name, channels? } object. Returns null for empty/invalid entries.
export function normalizeProposedInstrument(
  inst: ProposedInstrument | string | undefined | null,
): ProposedInstrument | null {
  if (typeof inst === 'string') {
    const name = inst.trim();
    return name ? { name } : null;
  }
  if (!inst || typeof inst !== 'object' || Array.isArray(inst)) return null;
  const name = typeof inst.name === 'string' ? inst.name.trim() : '';
  if (!name) return null;
  const out: ProposedInstrument = { name };
  const channels = Number(inst.channels);
  if (Number.isFinite(channels) && channels > 0) out.channels = Math.floor(channels);
  return out;
}

// ------------------------------------------------------------- matching ----

// Build the diff of proposed personnel against the existing rider lists.
// `existingMusicians` / `existingCrew` are the band's current rider cards
// (Musician[] / CrewMember[]). `teamMembers` is optional and unused for the
// match itself (matching is by normalized name against the rider lists); it is
// kept in the signature so the caller can pass the Team roster if a future
// refinement wants to reconcile against Team identities.
export function matchPersonnel(
  proposed: ProposedPersonnel | null | undefined,
  existingMusicians: Musician[],
  existingCrew: CrewMember[],
  _teamMembers?: unknown[],
): PersonnelDiff {
  const musicians: PersonnelMatch[] = [];
  const crew: PersonnelMatch[] = [];

  const musicianByName = new Map<string, Musician>();
  for (const m of existingMusicians || []) {
    if (m && m.name) musicianByName.set(normalizePersonName(m.name), m);
  }
  const crewByName = new Map<string, CrewMember>();
  for (const c of existingCrew || []) {
    if (c && c.name) crewByName.set(normalizePersonName(c.name), c);
  }

  const proposedMusicians = Array.isArray(proposed?.musicians) ? proposed.musicians : [];
  for (const p of proposedMusicians) {
    if (!p || typeof p !== 'object' || Array.isArray(p)) continue;
    const name = typeof p.name === 'string' ? p.name.trim() : '';
    if (!name) continue;
    const key = normalizePersonName(name);
        const existing = musicianByName.get(key);
        if (existing) {
          musicians.push(buildEnrichMatch('musician', p, existing));
        } else {
          const instruments = (Array.isArray(p.instruments) ? p.instruments : [])
            .map(normalizeProposedInstrument)
            .filter((i): i is ProposedInstrument => i !== null);
          musicians.push({
            kind: 'musician',
            action: 'add',
            fields: { name, ...(instruments.length > 0 ? { instruments } : {}) },
            conflicts: [],
                        proposed: p,
                      });
        }
  }

  const proposedCrew = Array.isArray(proposed?.crew) ? proposed.crew : [];
  for (const p of proposedCrew) {
    if (!p || typeof p !== 'object' || Array.isArray(p)) continue;
    const name = typeof p.name === 'string' ? p.name.trim() : '';
    if (!name) continue;
    const key = normalizePersonName(name);
    const existing = crewByName.get(key);
    if (existing) {
      crew.push(buildEnrichMatch('crew', p, existing));
    } else {
      crew.push({
        kind: 'crew',
        action: 'add',
        fields: { name, role: normalizeCrewRole(p.role) },
        conflicts: [],
        proposed: p,
      });
    }
  }

  return { musicians, crew };
}

// Build an 'enrich' match: only EMPTY fields of the existing card are proposed
// for writing; already-filled fields go to `conflicts` (never overwritten).
function buildEnrichMatch(
  kind: PersonnelKind,
  p: ProposedMusician | ProposedCrew,
  existing: Musician | CrewMember,
): PersonnelMatch {
  const fields: Record<string, unknown> = {};
  const conflicts: string[] = [];

  if (kind === 'musician') {
    const pm = p as ProposedMusician;
    // name: only fill if the existing card has no name (rare — matched by name).
    if (!existing.name) fields.name = pm.name;
    else conflicts.push('name');
    // instruments: only add instruments that are NOT already present (by
    // normalized name). Never overwrite an existing instrument.
    const existingNames = new Set(
      (existing.instruments || []).map((i) => normalizePersonName(i.name)),
    );
    const toAdd: ProposedInstrument[] = [];
    for (const inst of pm.instruments || []) {
      const norm = normalizeProposedInstrument(inst);
      if (!norm) continue;
      if (existingNames.has(normalizePersonName(norm.name))) {
        conflicts.push('instrument:' + norm.name);
      } else {
        toAdd.push(norm);
      }
    }
    if (toAdd.length > 0) fields.instruments = toAdd;
  } else {
      const pc = p as ProposedCrew;
      const crewCard = existing as CrewMember;
      if (!crewCard.name) fields.name = pc.name;
      else conflicts.push('name');
      const role = normalizeCrewRole(pc.role);
      if (role !== 'other' && !crewCard.role) fields.role = role;
      else if (role !== 'other' && crewCard.role) conflicts.push('role');
    }

  return { kind, action: 'enrich', targetId: existing.id, fields, conflicts, proposed: p };
}

// ------------------------------------------------------------- applying ----

// Apply the user's checked matches to a LOCAL copy of the existing lists.
// `checked` is a Set of match indices (or a predicate) — only matches the user
// explicitly checked are applied. A match whose `fields` is empty changes
// nothing. Returns NEW arrays (immutable) so the caller can diff / save.
//
// `checked` semantics: an array of booleans parallel to the flattened
// [musicians..., crew...] matches, OR a Set of the flattened indices. To keep
// it simple and testable, we accept a Set<number> of flattened indices.
export function applyPersonnelMatches(
  diff: PersonnelDiff,
  existingMusicians: Musician[],
  existingCrew: CrewMember[],
  checked: Set<number>,
): { musicians: Musician[]; crew: CrewMember[] } {
  const musicians = existingMusicians.map((m) => ({ ...m }));
  const crew = existingCrew.map((c) => ({ ...c }));

  const musicianByName = new Map<string, number>();
  musicians.forEach((m, i) => { if (m.name) musicianByName.set(normalizePersonName(m.name), i); });
  const crewByName = new Map<string, number>();
  crew.forEach((c, i) => { if (c.name) crewByName.set(normalizePersonName(c.name), i); });

  let flatIndex = 0;
  for (const match of diff.musicians) {
    if (checked.has(flatIndex)) applyMusicianMatch(match, musicians, musicianByName);
    flatIndex++;
  }
  for (const match of diff.crew) {
    if (checked.has(flatIndex)) applyCrewMatch(match, crew, crewByName);
    flatIndex++;
  }

  return { musicians, crew };
}

function applyMusicianMatch(
  match: PersonnelMatch,
  musicians: Musician[],
  byName: Map<string, number>,
): void {
  const fields = match.fields;
  if (!fields || Object.keys(fields).length === 0) return;

  if (match.action === 'add') {
    const m = createMusician({ name: String(fields.name || '') });
    const instruments = (fields.instruments as ProposedInstrument[] | undefined) || [];
    m.instruments = instruments.map((i) => createInstrument({ name: i.name }));
    m.order = musicians.length;
    musicians.push(m);
    if (m.name) byName.set(normalizePersonName(m.name), musicians.length - 1);
    return;
  }

  // enrich
  const idx = match.targetId
    ? musicians.findIndex((m) => m.id === match.targetId)
    : -1;
  if (idx === -1) return;
  const target = musicians[idx];
  if (typeof fields.name === 'string' && fields.name && !target.name) {
    target.name = fields.name;
  }
  const instruments = (fields.instruments as ProposedInstrument[] | undefined) || [];
  const existingNames = new Set((target.instruments || []).map((i) => normalizePersonName(i.name)));
  for (const inst of instruments) {
    if (existingNames.has(normalizePersonName(inst.name))) continue; // idempotent
    target.instruments = [...(target.instruments || []), createInstrument({ name: inst.name })];
    existingNames.add(normalizePersonName(inst.name));
  }
}

function applyCrewMatch(
  match: PersonnelMatch,
  crew: CrewMember[],
  byName: Map<string, number>,
): void {
  const fields = match.fields;
  if (!fields || Object.keys(fields).length === 0) return;

  if (match.action === 'add') {
    const c = createCrewMember({ name: String(fields.name || '') });
    if (typeof fields.role === 'string' && fields.role) c.role = fields.role as CrewMember['role'];
    c.order = crew.length;
    crew.push(c);
    if (c.name) byName.set(normalizePersonName(c.name), crew.length - 1);
    return;
  }

  const idx = match.targetId
    ? crew.findIndex((c) => c.id === match.targetId)
    : -1;
  if (idx === -1) return;
  const target = crew[idx];
  if (typeof fields.name === 'string' && fields.name && !target.name) {
    target.name = fields.name;
  }
  if (typeof fields.role === 'string' && fields.role && !target.role) {
    target.role = fields.role as CrewMember['role'];
  }
}

// ------------------------------------------------------------- counters ----

export interface PersonnelCounts {
  newMusicians: number;
  enrichedMusicians: number;
  newCrew: number;
  enrichedCrew: number;
  skipped: number;
}

// Count what the diff WOULD do if every match were checked (used for the
// "3 new musicians · 2 enriched · 1 skipped" summary line).
export function countPersonnel(diff: PersonnelDiff): PersonnelCounts {
  const counts: PersonnelCounts = {
    newMusicians: 0,
    enrichedMusicians: 0,
    newCrew: 0,
    enrichedCrew: 0,
    skipped: 0,
  };
  for (const m of diff.musicians) {
    if (m.action === 'add') counts.newMusicians++;
    else if (Object.keys(m.fields).length > 0) counts.enrichedMusicians++;
    else counts.skipped++;
  }
  for (const c of diff.crew) {
    if (c.action === 'add') counts.newCrew++;
    else if (Object.keys(c.fields).length > 0) counts.enrichedCrew++;
    else counts.skipped++;
  }
  return counts;
}
